import React from "react";
import { useMemo, useState } from "react";
import { trpc } from "~/lib/trpc";
import { Group } from "@visx/group";
import { scaleTime, scaleLinear } from "@visx/scale";
import { AxisBottom, AxisLeft } from "@visx/axis";
import { LinePath, AreaClosed } from "@visx/shape";
import { ParentSize } from "@visx/responsive";
import { curveMonotoneX } from "@visx/curve";
import { localPoint } from "@visx/event";
import { bisector, extent } from "d3-array";
import { formatEur } from "~/lib/utils";
import { useT } from "~/lib/i18n";

interface MarketChartProps {
  brandId: string | null | undefined;
  modelId: string | null | undefined;
  country: string | null;
}

export function MarketChart({ brandId, modelId, country }: MarketChartProps): React.JSX.Element {
  const { t } = useT();
  const [expanded, setExpanded] = useState(false);
  const q = trpc.charts.marketDaily.useQuery(
    brandId && modelId
      ? {
          brandId,
          modelId,
          country: country ?? undefined,
          days: 90,
        }
      : (undefined as never),
    { enabled: !!brandId && !!modelId },
  );

  return (
    <div className="sticky bottom-0 z-10 border-t border-[var(--divider-strong)] bg-[color-mix(in_oklab,var(--surface-page)_92%,transparent)] backdrop-blur-md">
      <div className="w-full max-w-[1440px] mx-auto px-4 sm:px-6">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between py-2 hover:opacity-80 transition-opacity"
        >
          <span className="mono text-[10px] sm:text-[10.5px] uppercase tracking-[0.12em] text-ink-tertiary">
            {t("web.chart.kicker")}
          </span>
          <div className="flex items-center gap-2">
            <Trend30d data={q.data ?? []} />
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className={`text-ink-tertiary transition-transform ${expanded ? "rotate-180" : ""}`}
            >
              <path d="M18 15l-6-6-6 6" />
            </svg>
          </div>
        </button>
        {expanded && (
          <div className="h-[120px] sm:h-[130px] pb-2">
            <ParentSize debounceTime={50}>
              {({ width, height }) => <Chart width={width} height={height} data={q.data ?? []} />}
            </ParentSize>
          </div>
        )}
      </div>
    </div>
  );
}

type Row = {
  day: string | Date;
  median: number | null;
  p10: number | null;
  p90: number | null;
};

function Trend30d({ data }: { data: Row[] }): React.JSX.Element {
  const { t } = useT();
  const last30 = data.slice(-30);
  const first = last30[0]?.median;
  const last = last30[last30.length - 1]?.median;
  if (first == null || last == null) {
    return <span className="text-[11px] text-ink-tertiary mono">—</span>;
  }
  const delta = ((last - first) / first) * 100;
  const dir = delta > 0.5 ? "up" : delta < -0.5 ? "down" : "flat";
  const cls =
    dir === "up" ? "text-deal-over" : dir === "down" ? "text-deal-super" : "text-ink-tertiary";
  const arrow = dir === "up" ? "▲" : dir === "down" ? "▼" : "→";
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] mono font-medium ${cls}`}>
      <span>{arrow}</span>
      <span>{Math.abs(delta).toFixed(1)}% / 30j</span>
    </span>
  );
}

type Point = { day: Date; median: number; p10: number | null; p90: number | null };

const bisectDate = bisector<Point, Date>((d) => d.day).left;

function Chart({ width, height, data }: { width: number; height: number; data: Row[] }): React.JSX.Element | null {
  const { t } = useT();
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const points: Point[] = useMemo(
    () =>
      data
        .map((r) => ({
          day: typeof r.day === "string" ? new Date(r.day) : r.day,
          median: r.median!,
          p10: r.p10,
          p90: r.p90,
        }))
        .filter((r) => r.median != null),
    [data],
  );

  const canRender = width >= 80 && height >= 40 && points.length > 0;

  const layout = useMemo(() => {
    if (!canRender) return null;
    const compact = width < 420;
    const margin = {
      top: 6,
      right: compact ? 6 : 14,
      bottom: compact ? 24 : 28,
      left: compact ? 44 : 60,
    };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;
    const [tMin, tMax] = extent(points, (p) => p.day) as [Date, Date];
    const allPrices = points.flatMap((p) => [p.p10, p.median, p.p90].filter((v): v is number => v != null));
    const [pMin, pMax] = extent(allPrices) as [number, number];
    const xScale = scaleTime<number>({ domain: [tMin, tMax], range: [0, innerW] });
    const yScale = scaleLinear<number>({
      domain: [pMin * 0.95, pMax * 1.05],
      range: [innerH, 0],
      nice: true,
    });
    return { compact, margin, innerW, innerH, xScale, yScale };
  }, [canRender, width, height, points]);

  if (!canRender || !layout) {
    return (
      <div className="h-full flex items-center justify-center text-xs text-ink-tertiary">
        {t("web.chart.empty")}
      </div>
    );
  }

  const { compact, margin, innerW, innerH, xScale, yScale } = layout;
  const hoverPoint = hoverIdx != null ? points[hoverIdx] : null;

  const handleMouseMove = (event: React.MouseEvent<SVGRectElement>) => {
    const coords = localPoint(event);
    if (!coords) return;
    const x = coords.x - margin.left;
    const date = xScale.invert(x);
    const idx = bisectDate(points, date, 1);
    const d0 = points[idx - 1];
    const d1 = points[idx];
    if (!d0) { setHoverIdx(0); return; }
    if (!d1) { setHoverIdx(points.length - 1); return; }
    const chosen = date.getTime() - d0.day.getTime() > d1.day.getTime() - date.getTime() ? idx : idx - 1;
    setHoverIdx(chosen);
  };

  return (
    <svg width={width} height={height} className="select-none">
      <Group left={margin.left} top={margin.top}>
        <AreaClosed
          data={points.filter((p) => p.p10 != null && p.p90 != null)}
          x={(d) => xScale(d.day)}
          y={(d) => yScale(d.p90!)}
          y0={(d) => yScale(d.p10!)}
          yScale={yScale}
          fill="var(--accent)"
          fillOpacity={0.06}
          curve={curveMonotoneX}
        />
        <LinePath
          data={points}
          x={(d) => xScale(d.day)}
          y={(d) => yScale(d.median)}
          stroke="var(--accent)"
          strokeWidth={2}
          curve={curveMonotoneX}
        />
        {/* Hover vertical line + dot */}
        {hoverPoint && (
          <>
            <line
              x1={xScale(hoverPoint.day)}
              x2={xScale(hoverPoint.day)}
              y1={0}
              y2={innerH}
              stroke="var(--ink-tertiary)"
              strokeWidth={1}
              strokeDasharray="3 3"
              opacity={0.5}
            />
            <circle
              cx={xScale(hoverPoint.day)}
              cy={yScale(hoverPoint.median)}
              r={4}
              fill="var(--accent)"
              stroke="var(--surface-card)"
              strokeWidth={2}
            />
          </>
        )}
        {/* Invisible rect for mouse tracking */}
        <rect
          width={innerW}
          height={innerH}
          fill="transparent"
          onMouseMove={handleMouseMove}
          onMouseLeave={() => setHoverIdx(null)}
        />
        <AxisBottom
          top={innerH}
          scale={xScale}
          stroke="var(--divider-weak)"
          tickStroke="var(--divider-strong)"
          numTicks={compact ? 3 : 5}
          tickLabelProps={() => ({
            fill: "var(--ink-tertiary)",
            fontSize: compact ? 9 : 10,
            fontFamily: "var(--font-mono)",
            textAnchor: "middle" as const,
            dy: "0.6em",
          })}
        />
        <AxisLeft
          scale={yScale}
          stroke="var(--divider-weak)"
          tickStroke="var(--divider-strong)"
          numTicks={compact ? 2 : 3}
          tickFormat={(v) => formatEur(Number(v), { compact: true })}
          tickLabelProps={() => ({
            fill: "var(--ink-tertiary)",
            fontSize: compact ? 9 : 10,
            fontFamily: "var(--font-mono)",
            textAnchor: "end" as const,
            dx: "-0.4em",
            dy: "0.3em",
          })}
        />
      </Group>
      {/* Tooltip */}
      {hoverPoint && (
        <foreignObject
          x={Math.min(xScale(hoverPoint.day) + margin.left + 8, width - 110)}
          y={Math.max(yScale(hoverPoint.median) + margin.top - 36, 0)}
          width={100}
          height={32}
        >
          <div className="inline-flex items-center gap-1.5 h-7 px-2 rounded-md bg-[var(--surface-card)] border border-[var(--divider-strong)] shadow-sm">
            <span className="text-[10px] mono text-ink-primary font-semibold">
              {formatEur(hoverPoint.median, { compact: true })}
            </span>
            <span className="text-[9px] mono text-ink-tertiary">
              {hoverPoint.day.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
            </span>
          </div>
        </foreignObject>
      )}
    </svg>
  );
}
